import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";
import DynamicTable from "../../../../components/Dynamic-table";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import WordRichTextEditor from "../../../../components/OPD/usg-echo/WordRichTextEditor";

const ReportTemplates: React.FC = () => {
  const [editData, setEditData] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    htmlContent: "",
    reportName: "",
  });

  const navigate = useNavigate();
  const apiBase = `${process.env.REACT_APP_OPD_API_URL}/ultra-echo`;
  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBase}/get-template`, {
        headers: authHeaders,
      });
      if (response.data?.isSuccess) {
        setTemplates(response.data?.data || []);
      } else {
        toast.error(response.data?.message || "Failed to fetch templates");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Error fetching report templates"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const filteredTemplates = useMemo(() => {
    const search = searchInput.trim().toLowerCase();
    if (!search) return templates;
    return templates.filter((item) =>
      [item?.title, item?.reportName]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search))
    );
  }, [templates, searchInput]);

  const paginatedTemplates = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredTemplates.slice(start, start + rowsPerPage);
  }, [filteredTemplates, page, rowsPerPage]);

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      htmlContent: "",
      reportName: "",
    });
    setEditData(null);
  };

  const handleEditClick = (id: string, rowData: any) => {
    setEditData(id);
    setFormData({
      title: rowData.title || "",
      htmlContent: rowData.htmlContent || "",
      reportName: rowData.reportName || "",
    });
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      toast.error("Please enter a template title");
      return false;
    }
    if (
      !formData.htmlContent.trim() ||
      formData.htmlContent === "<p><br></p>"
    ) {
      toast.error("Please enter template content");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (editData) {
        const response = await axios.put(
          `${apiBase}/update-template/${editData}`,
          {
            title: formData.title,
            htmlContent: formData.htmlContent,
            note: "",
            reportName: formData.reportName,
          },
          { headers: authHeaders }
        );
        if (!response.data?.isSuccess) {
          toast.error(response.data?.message || "Failed to update template");
        } else {
          toast.success(
            response.data?.message || "Template updated successfully"
          );
          resetForm();
          fetchTemplates();
        }
      } else {
        const response = await axios.post(
          `${apiBase}/create-template`,
          {
            extra: {},
            title: formData.title,
            htmlContent: formData.htmlContent,
            note: "",
            reportName: formData.reportName,
          },
          { headers: authHeaders }
        );
        if (!response.data?.isSuccess) {
          toast.error(response.data?.message || "Failed to create template");
        } else {
          toast.success(
            response.data?.message || "Template created successfully"
          );
          resetForm();
          fetchTemplates();
        }
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || error.message || "Something went wrong"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteClick = (id: string) => {
    setIsDeleteModalOpen(true);
    setSelectedItemId(id);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authorization token is missing");
        return;
      }

      const response = await axios.delete(`${apiBase}/${selectedItemId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.isSuccess) {
        toast.success(response.data?.message || "Template deleted successfully");
        if (editData === selectedItemId) {
          resetForm();
        }
        fetchTemplates();
      } else {
        toast.error(response.data?.message || "Failed to delete template");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || error.message || "Delete failed"
      );
    }
    setIsDeleteModalOpen(false);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setPage(0);
  };

  return (
    <Box>
      <Box
        sx={{
          background: "#f4f4f4",
          borderRadius: 2,
          padding: "1px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          Report Templates
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            label="Search"
            placeholder="Search templates..."
            variant="outlined"
            value={searchInput}
            onChange={handleSearchInputChange}
            size="small"
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon sx={{ color: "#055FFC" }} />
                </IconButton>
              ),
            }}
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate("/opd-admin")}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              p: 3,
              boxShadow: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              {editData ? "Update Template" : "Add New Template"}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                size="small"
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Report Name"
                name="reportName"
                value={formData.reportName}
                onChange={handleChange}
                sx={{ width: "28%", minWidth: 140 }}
              />
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: "block" }}>
              Template Content
            </Typography>
            <Box
              sx={{
                mb: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                overflow: "hidden",
                "& .jodit-container:not(.jodit_inline)": {
                  border: "none",
                },
                "& .jodit-toolbar__box": {
                  border: "none",
                  borderBottom: "1px solid #e0e0e0",
                  backgroundColor: "#f8f9fa",
                },
                "& .jodit-workplace": {
                  minHeight: "380px",
                },
              }}
            >
              <WordRichTextEditor
                value={formData.htmlContent}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, htmlContent: value }))
                }
                minHeight={380}
                placeholder="Define the report template content..."
              />
            </Box>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? "Saving..." : editData ? "Update" : "Save"}
            </Button>
            {editData && (
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={resetForm}
                sx={{ mt: 1 }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <DynamicTable
            headers={["SN", "Title", "Report Name", "Actions"]}
            data={paginatedTemplates.map((item: any, index: number) => [
              page * rowsPerPage + index + 1,
              item?.title,
              item?.reportName || "-",
            ])}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRows={filteredTemplates.length}
            actionButtons={[
              {
                icon: (
                  <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />
                ),
                onClick: (rowIndex: number) =>
                  handleEditClick(
                    paginatedTemplates[rowIndex]?._id,
                    paginatedTemplates[rowIndex]
                  ),
              },
              {
                icon: (
                  <DeleteIcon sx={{ color: "#FF5454", cursor: "pointer" }} />
                ),
                onClick: (rowIndex: number) =>
                  handleDeleteClick(paginatedTemplates[rowIndex]?._id),
              },
            ]}
          />
        </Grid>
      </Grid>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={handleDeleteModalClose}
        handleConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default ReportTemplates;
